import { cloneDeep, filter, find, forEach, get, isEqual, keyBy, map, mapValues, pick } from 'lodash';
import { IsNotEmpty, IsOptional } from 'class-validator';

import * as sharedTypes from 'src/actions/shared/sharedTypes';
import createCollectionResource, { UpdateOptions } from 'src/actions/shared/createResource';
import { DomainError } from 'src/models/DomainError';
import { DomainError as StandardDomainError, Unauthorized } from '@ad-scripts/application/lib/error';

import { Endpoint } from 'src/actions/endpoints/endpointsResource';
import { EndpointsSections } from 'src/presentation/components/pages/channels/entryPoints/components/EntryPointConstants';
import { EndpointTypes } from 'src/models/Channels/endpointType';
import EntryPointsEntity from './EntryPointsEntity';
import generateId from 'src/lib/generateId';
import channelsConfigurationResource, {
  getDefaultChannelConfiguration,
} from 'src/actions/channels/channelsConfigurationResource';
import { translateErrors } from 'src/adapters/validateType';
import { toastError, toastSuccess } from 'src/actions/toast';
import RoutingGroup from 'src/models/RoutingGroup/RoutingGroup';
import { logout } from '../auth/authSlice';

const LABELS = Object.freeze({
  selectAnotherInbox: 'Select another inbox',
  selectEndpoint: 'Select an Entry Point',
  selectInbox: 'Select an Inbox',
});

export class CommunicationQueue {
  endpointId: string;
  id: string;
  name?: string;

  @IsNotEmpty()
  routingGroupId: string;

  @IsOptional()
  routingSelector?: string;

  @IsOptional()
  slaSeconds?: number;
}

const communicationQueuesResource = createCollectionResource<CommunicationQueue>({
  name: 'communicationQueues',
  endpoint: 'v1/orgs/:orgId/configuration/communication-queues/:id',
  type: CommunicationQueue,
  reducers: {
    entryPointsUpdateErrors(state, { payload }) {
      if (!state.entryPointsErrors) {
        state.entryPointsErrors = {};
      }
      state.entryPointsErrors = { ...state.entryPointsErrors, [payload.type]: payload.errors };
    },
    resetEntryPointsErrors(state, { payload }) {
      if (!state.entryPointsErrors) {
        state.entryPointsErrors = {};
      }
      state.persistingState = 'unstarted';

      let sectionErrors: any = [];
      if (payload.attr) {
        sectionErrors = filter(state.entryPointsErrors[payload.sectionType], error => error.attr !== payload.attr);
      }
      state.entryPointsErrors = { ...state.entryPointsErrors, [payload.sectionType]: sectionErrors };
    },
  },
});

const createNewCommunicationQueue: sharedTypes.AppThunk =
  (endpoint: Endpoint, opts: UpdateOptions) => async dispatch => {
    const [routingGroupId, slaSeconds] = (await dispatch(getDefaultChannelConfiguration(endpoint.type))) as [
      string,
      number
    ];
    const communicationQueue = { id: generateId(), endpointId: endpoint.id, routingGroupId, slaSeconds };
    dispatch(communicationQueuesResource.effects.add({}, toCommunicationQueue(communicationQueue), opts));
  };

export const toCommunicationQueue = (commQueue: Partial<CommunicationQueue>): CommunicationQueue => {
  // if slaSeconds === empty string => 0
  return pick({ ...commQueue, slaSeconds: commQueue.slaSeconds || 0 }, [
    'endpointId',
    'id',
    'name',
    'routingGroupId',
    'routingSelector',
    'slaSeconds',
  ]) as CommunicationQueue;
};

const getActiveRoutingGroupMap = (routingGroups: RoutingGroup[]): Record<string, RoutingGroup> => {
  const activeRoutingGroups = filter(routingGroups, routingGroup => !routingGroup.disabled);
  return mapValues(keyBy(activeRoutingGroups, 'id'));
};

const getInboxError = (attr: string, routingGroupId?: string): DomainError => {
  return routingGroupId
    ? { attr, code: 'invalid_state', detail: LABELS.selectAnotherInbox }
    : { attr, code: 'blank', detail: LABELS.selectInbox };
};

const _validateEntryPoints = (entryPointEntity: EntryPointsEntity, routingGroups: RoutingGroup[]): void | never => {
  const activeRoutingGroupMap = getActiveRoutingGroupMap(routingGroups);

  const exceptions = get(entryPointEntity, 'exceptionsGrouped', []);

  const domainErrors: DomainError[] = [];

  forEach(exceptions, (exception: any, index) => {
    const routingGroup = activeRoutingGroupMap[exception.routingGroupId];
    if (!routingGroup) {
      domainErrors.push(getInboxError(`exceptionsGrouped[${index}].routingGroupId`, exception.routingGroupId));
    }

    if (!exception.communicationQueues.length) {
      domainErrors.push({
        attr: `exceptionsGrouped[${index}].communicationQueues`,
        code: 'blank',
        detail: LABELS.selectEndpoint,
      });
    }
  });

  const defaultRoutingGroup = activeRoutingGroupMap[entryPointEntity.routingGroupId];
  if (!defaultRoutingGroup) {
    domainErrors.push(getInboxError('routingGroupId', entryPointEntity.routingGroupId));
  }

  if (domainErrors.length) {
    throw new StandardDomainError(domainErrors);
  }
};

const validateEntryPoints: sharedTypes.AppThunk =
  (type: EndpointTypes, attrs: EntryPointsEntity) => (dispatch, getState) => {
    try {
      _validateEntryPoints(attrs, getState().routingGroups.values);
    } catch (err: unknown) {
      dispatch(
        communicationQueuesResource.actions.entryPointsUpdateErrors({
          type,
          errors: translateErrors((err as StandardDomainError).errors),
        })
      );
      return false;
    }
    return true;
  };

const updateEntryPoints: sharedTypes.AppThunk =
  (attrs: EntryPointsEntity) =>
  async (dispatch, getState, { gateways }) => {
    const { type: channelId } = attrs;

    let channelName;
    if (channelId.startsWith(EndpointTypes.CUSTOM_CHANNEL)) {
      const { values: customChannels } = getState().customChannels;
      const customChannelId = channelId.split(':')[1];
      const customChannel = find(customChannels, { id: customChannelId });

      channelName = customChannel?.name;
    } else {
      channelName = EndpointsSections[channelId].displayName;
    }

    try {
      try {
        _validateEntryPoints(attrs, getState().routingGroups.values);
      } catch (err) {
        dispatch(
          communicationQueuesResource.actions.entryPointsUpdateErrors({
            type: channelId,
            errors: translateErrors((err as StandardDomainError).errors),
          })
        );
        return;
      }

      const previousConfiguration = getState().channelsConfiguration.value;
      const previousCommunicationQueues = getState().communicationQueues.values;

      const areDefaultSettingsChanged =
        !isEqual(get(attrs, 'routingGroupId'), get(previousConfiguration, `${channelId}.defaultInbox`)) ||
        !isEqual(get(attrs, 'slaSeconds'), get(previousConfiguration, `${channelId}.defaultSLASeconds`));

      const changedItems = getChangedItems(attrs, previousCommunicationQueues);

      dispatch(communicationQueuesResource.actions.updateStarted({}));
      dispatch(channelsConfigurationResource.actions.updateStarted({}));

      if (areDefaultSettingsChanged) {
        const newConfiguration = {
          ...previousConfiguration,
          [channelId]: {
            defaultInbox: attrs.routingGroupId,
            ...(attrs.slaSeconds ? { defaultSLASeconds: attrs.slaSeconds } : {}),
          },
        };
        await gateways.channelsConfiguration.replace(newConfiguration);
      }

      if (changedItems.length) {
        await Promise.all(map(changedItems, attr => gateways.communicationQueues.update({ id: attr.id }, attr)));
      }

      dispatch(communicationQueuesResource.actions.updateSuccess({}));
      dispatch(channelsConfigurationResource.actions.updateSuccess({}));

      const toastMessage = `Entry Points for ${channelName}  have been edited.`;
      dispatch(toastSuccess(toastMessage));
    } catch (err) {
      if (err instanceof StandardDomainError) {
        dispatch(communicationQueuesResource.actions.addError(err.errors));
      } else if (err instanceof Unauthorized) {
        dispatch(logout());
      } else {
        dispatch(communicationQueuesResource.actions.addRequestError(err));
      }

      dispatch(toastError(`Unable to save the changes to the Entry Points for ${channelName}. Please try again.`));
    }
  };

const getChangedItems = (workingState, clonedCommunicationQueues) => {
  const exceptionsGrouped = get(workingState, 'exceptionsGrouped');
  const defaults = get(workingState, 'defaultsIds');
  const { routingGroupId: defaultRoutingGroupId, slaSeconds: defaultSlaSeconds } = workingState;

  const changes: CommunicationQueue[] = [];
  const exceptionIds: string[] = [];

  forEach(exceptionsGrouped, group => {
    const communicationQueues = filter(clonedCommunicationQueues, commQ =>
      group.communicationQueues.includes(commQ.id)
    );
    forEach(communicationQueues, communicationQueue => {
      const arePropsDifferent =
        group.slaSeconds !== communicationQueue.slaSeconds ||
        group.routingGroupId !== communicationQueue.routingGroupId;

      if (arePropsDifferent) {
        const newCommunicationQueue = cloneDeep(communicationQueue);
        newCommunicationQueue.slaSeconds = group.slaSeconds;
        newCommunicationQueue.routingGroupId = group.routingGroupId;

        changes.push(toCommunicationQueue(newCommunicationQueue));
      }
    });
    exceptionIds.push(...group.communicationQueues);
  });

  const communicationQueuesWithDefaults = filter(clonedCommunicationQueues, commQ => defaults.includes(commQ.id));

  forEach(communicationQueuesWithDefaults, communicationQueue => {
    const arePropsDifferent =
      defaultSlaSeconds !== communicationQueue.slaSeconds ||
      defaultRoutingGroupId !== communicationQueue.routingGroupId;

    if (arePropsDifferent) {
      const newCommunicationQueue = cloneDeep(communicationQueue);
      newCommunicationQueue.slaSeconds = defaultSlaSeconds;
      newCommunicationQueue.routingGroupId = defaultRoutingGroupId;
      changes.push(toCommunicationQueue(newCommunicationQueue));
    }
  });

  return changes;
};

communicationQueuesResource.effects.createNewCommunicationQueue = createNewCommunicationQueue;
communicationQueuesResource.effects.validateEntryPoints = validateEntryPoints;
communicationQueuesResource.effects.updateEntryPoints = updateEntryPoints;

export default communicationQueuesResource;
